import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FocalPoint, Tooltip } from '@yola/ws-ui';
import throttle from 'lodash.throttle';
import { utils, view } from '@yola/ws-sdk';
import ControlPane from 'src/js/modules/control-pane/components/control-pane';
import TriggerEventTracker from 'src/js/modules/utils/trigger-event-tracker';
import customUI from 'src/js/modules/custom-ui';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import topbar from 'src/js/modules/topbar';
import segment from 'src/js/modules/analytics/segment';
import withFocalPointContext from '../hoc/with-focal-point-context';
import constants from '../constants/common';
import operationTypes from '../constants/operation-types';
import helpers from '../helpers';
import ids from '../constants/triggers-ids';
import useFocalPoint from '../hooks/use-focal-point';
import { getSettings } from '../settings';
import { EDITOR_TOPBAR_DEFAULT_HEIGHT } from '../../topbar/constants/index';

const {
  track,
  constants: { events },
} = segment;

const { ZOOM_STEP, BACKGROUND_IMAGE, SUBMIT_TRIGGER, FOCAL_POINT_AREA_TRIGGER } = constants;

/* eslint no-shadow: "off" */
/* eslint no-param-reassign: "off" */
/* eslint no-console: "off" */

function BackgroundImageContainer({
  elementId,
  onOverlayClick = utils.noop,
  onSubmit = utils.noop,
  onDrag: onDragProp = utils.noop,
  onDragStart: onDragStartProp = utils.noop,
  onDragEnd: onDragEndProp = utils.noop,
  onScale = utils.noop,
  width: viewportWidth = null,
  height: viewportHeight = null,
  orientation = null,
  onCancel = utils.noop,
  shouldApplyChangesOnSubmit = true,
  preserveSharedData = false,
}) {
  const scaleBeforeChange = useRef(null);
  const previousImagePosition = useRef({ x: 0, y: 0 });

  const settings = getSettings();
  const topbarHeight = useSelector(topbar.selectors.getHeight);

  const [imageOffset, setImageOffset] = useState({ x: 50, y: 50 });
  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const onPositionChange = useCallback(
    throttle(({ x, y }) => {
      setImageOffset({ x, y });
    }, 200),
    []
  );

  const onDrag = useCallback(
    (...args) => {
      const [offsetInPercents] = args;

      onDragProp(...args);
      onPositionChange({
        x: offsetInPercents.xInPercents,
        y: offsetInPercents.yInPercents,
      });
    },
    [onDragProp, onPositionChange]
  );

  const onInitialImageLoad = useCallback(
    ({ initialData, imageNode }) => {
      const { height: initialContainerHeight } = helpers.getContainerBounds(imageNode);
      initialData.current.containerHeight = initialContainerHeight;

      track(events.FOCAL_POINT_SETTINGS_DISPLAYED, {
        ...getDefaultTraits(elementId),
        imageType: BACKGROUND_IMAGE,
      });
    },
    [elementId]
  );

  const onFitImageSize = useCallback(
    ({ oldZoom, newZoom }) => {
      track(events.FOCAL_POINT_FIT_IN_BUTTON_CLICKED, {
        ...getDefaultTraits(elementId),
        oldZoom,
        newZoom,
        imageType: BACKGROUND_IMAGE,
      });
    },
    [elementId]
  );

  const onDragStart = useCallback(
    (...args) => {
      const [, , { position }] = args;
      previousImagePosition.current = {
        x: position.x,
        y: position.y,
      };
      onDragStartProp(...args);
    },
    [onDragStartProp]
  );

  const onDragEnd = useCallback(
    (...args) => {
      onDragEndProp(...args);
      const [{ x, y }] = args;
      const { x: prevX, y: prevY } = previousImagePosition.current;

      if (prevX !== x || prevY !== y) {
        track(events.FOCAL_POINT_IMAGE_POSITION_ADJUSTED, {
          ...getDefaultTraits(elementId),
          imageType: BACKGROUND_IMAGE,
        });
      }
      previousImagePosition.current = { x, y };
    },
    [elementId, onDragEndProp]
  );

  const {
    position,
    scale,
    zoom,
    imageBounds,
    initialData,
    controlsRef,
    isLoadingImage,
    imageNode,
    imageComputedStyles,
    handleImageDrag,
    handleImageDragStart,
    handleImageDragEnd,
    handleScale,
    handleFitImageSize,
    currentSource,
  } = useFocalPoint({
    elementId,
    onInitialImageLoad,
    onFitImageSize,
    viewportWidth,
    viewportHeight,
    orientation,
    onDrag,
    onDragStart,
    onDragEnd,
    onScale,
    preserveSharedData,
    getInitialScrollPosition: (isHigherThanViewport) => ({
      sideToScroll: isHigherThanViewport ? 'top' : 'center',
    }),
  });

  useEffect(() => {
    const [x, y] = helpers.getPosition(imageNode);

    setImageOffset({ x, y });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const handleCancel = useCallback(() => {
    if (isLoadingImage.current) return;
    const {
      scale: initialScale,
      position: initialPosition,
      source: initialSource,
      isMigrated,
    } = initialData.current;

    helpers.setScale(imageNode, initialScale);
    helpers.setPosition(
      imageNode,
      helpers.createCssPositionString(initialPosition.x, initialPosition.y)
    );
    if (currentSource !== initialSource) {
      helpers.setCurrentImageUrl(imageNode, initialSource);
    }

    if (isMigrated) {
      imageNode.setAttribute(constants.OLD_LOGIC_ATTRIBUTE, true);
    }

    track(events.FOCAL_POINT_SETTINGS_CANCELLED, {
      ...getDefaultTraits(elementId),
      imageType: BACKGROUND_IMAGE,
    });

    customUI.operations.hide();
    onOverlayClick();
    onCancel();
  }, [isLoadingImage, initialData, imageNode, currentSource, elementId, onOverlayClick, onCancel]);

  const handleSubmit = useCallback(() => {
    const [x, y] = helpers.getPosition(imageNode);
    const { isMigrated, source: initialSource } = initialData.current;
    const isSourceChanged = currentSource !== initialSource;
    const setPositionOperation = helpers.getViewOperation(
      operationTypes.POSITION,
      elementId,
      helpers.createCssPositionString(x, y)
    );
    const setScaleOperation = helpers.getViewOperation(operationTypes.SCALE, elementId, scale);

    const operations = [setPositionOperation, setScaleOperation];

    if (isMigrated) {
      operations.push([
        view.operations.removeElementAttribute,
        [elementId, constants.OLD_LOGIC_ATTRIBUTE],
      ]);
    }

    if (isSourceChanged) {
      operations.unshift(helpers.getViewOperation(operationTypes.SRC, elementId, currentSource));
    }

    if (shouldApplyChangesOnSubmit) {
      view.operations.bulkViewOperations(operations);
    }

    onSubmit({ operations });
    customUI.operations.hide();
    Tooltip.hide();
  }, [
    imageNode,
    shouldApplyChangesOnSubmit,
    initialData,
    onSubmit,
    elementId,
    scale,
    currentSource,
  ]);

  const withSubmitAnalyticsEvent = useCallback(
    (cb, triggerId) => () => {
      const { scale: initialScale, position: initialPosition } = initialData.current;
      cb();

      track(events.FOCAL_POINT_SETTINGS_SUBMITTED, {
        ...getDefaultTraits(elementId),
        oldZoom: initialScale,
        newZoom: scale,
        zoomAdjusted: initialScale !== scale,
        positionAdjusted: initialPosition.x !== position.x || initialPosition.y !== position.y,
        imageType: BACKGROUND_IMAGE,
        triggerId,
      });
    },
    [elementId, position, initialData, scale]
  );

  const saveInitialSliderValueBeforeChange = useCallback(() => {
    // RCSlider component do not guarantee that `onBeforeChange` callback would run once
    // before `onAfterChange`. We've faced a use case when it's called once again between
    // `onChange`  and `onAfterChange` so we get wrong value. That's why we set initial
    // value only if it's empty, and empty it on purpose inside `onAfterChange` callback handler.
    if (scaleBeforeChange.current === null) {
      scaleBeforeChange.current = helpers.getScale(imageNode);
    }
  }, [imageNode]);

  const trackSliderChanges = useCallback(() => {
    if (scaleBeforeChange.current === null) return;

    const scale = helpers.getScale(imageNode);
    if (scale !== scaleBeforeChange.current) {
      track(events.FOCAL_POINT_ZOOM_SLIDER_ADJUSTED, {
        ...getDefaultTraits(elementId),
        oldZoom: scaleBeforeChange.current,
        newZoom: scale,
        imageType: BACKGROUND_IMAGE,
      });
    }
    scaleBeforeChange.current = null;
  }, [imageNode, elementId]);

  const imageSubmitControl = helpers.getSubmitControl({
    id: ids.BACKGROUND_IMAGE_SUBMIT,
    onClick: withSubmitAnalyticsEvent(handleSubmit, SUBMIT_TRIGGER),
  });

  const fitControl = helpers.getFitControl({
    id: ids.BACKGROUND_IMAGE_FIT_TRIGGER,
    onClick: handleFitImageSize,
  });

  const imageContainerControls = useMemo(
    () => [
      helpers.getZoomOutControl({
        id: ids.BACKGROUND_IMAGE_ZOOM_OUT_TRIGGER,
        onClick: () => {
          const oldZoom = helpers.getScale(imageNode);
          Tooltip.hide();
          handleScale(zoom - ZOOM_STEP);
          track(events.FOCAL_POINT_ZOOM_OUT_BUTTON_CLICKED, {
            ...getDefaultTraits(elementId),
            oldZoom,
            newZoom: helpers.getScale(imageNode),
            imageType: BACKGROUND_IMAGE,
          });
        },
      }),
      helpers.getZoomSliderControl({
        id: ids.BACKGROUND_IMAGE_ZOOM_SLIDER,
        scale,
        zoom,
        onChange: (...args) => {
          saveInitialSliderValueBeforeChange();
          handleScale(...args);
        },
        onAfterChange: trackSliderChanges,
        onBeforeChange: saveInitialSliderValueBeforeChange,
      }),
      helpers.getZoomInControl({
        id: ids.BACKGROUND_IMAGE_ZOOM_IN_TRIGGER,
        onClick: () => {
          const oldZoom = helpers.getScale(imageNode);
          Tooltip.hide();
          handleScale(zoom + ZOOM_STEP);
          track(events.FOCAL_POINT_ZOOM_IN_BUTTON_CLICKED, {
            ...getDefaultTraits(elementId),
            oldZoom,
            newZoom: helpers.getScale(imageNode),
            imageType: BACKGROUND_IMAGE,
          });
        },
      }),
      fitControl,
      imageSubmitControl,
    ],
    [
      elementId,
      fitControl,
      handleScale,
      imageNode,
      imageSubmitControl,
      saveInitialSliderValueBeforeChange,
      trackSliderChanges,
      zoom,
      scale,
    ]
  );

  const targetElementBounds = useMemo(() => {
    if (!imageBounds) return null;

    return imageBounds.height + topbarHeight > viewportHeight
      ? {
          ...imageBounds,
          top: imageBounds.top + (topbarHeight - EDITOR_TOPBAR_DEFAULT_HEIGHT),
          height: imageBounds.height - (topbarHeight - EDITOR_TOPBAR_DEFAULT_HEIGHT),
        }
      : imageBounds;
  }, [imageBounds, topbarHeight, viewportHeight]);

  const controlsProps = useMemo(() => {
    if (!targetElementBounds) return null;

    return {
      position: helpers.getControlsPosition(targetElementBounds, false, imageContainerControls),
    };
  }, [targetElementBounds, imageContainerControls]);

  if (imageNode && !helpers.hasBackgroundContainer(imageNode)) {
    console.warn(
      `You can't use focal point with any other node type except ${settings.backgroundContainer}`
    );
    return null;
  }

  if (!imageBounds) return null;

  const {
    position: { offsetX, offsetY },
  } = controlsProps;
  const controls = (
    <TriggerEventTracker>
      <ControlPane
        controlPaneRef={controlsRef}
        offsetX={offsetX}
        offsetY={offsetY}
        direction="none"
        content={imageContainerControls}
        expand="down"
        className="ws-focal-point-controls"
      />
    </TriggerEventTracker>
  );

  return (
    <FocalPoint
      controls={controls}
      onOverlayClick={handleCancel}
      onOverlayDoubleClick={withSubmitAnalyticsEvent(handleSubmit, FOCAL_POINT_AREA_TRIGGER)}
      onDrag={handleImageDrag}
      onDragStart={handleImageDragStart}
      onDragEnd={handleImageDragEnd}
      targetElementBounds={targetElementBounds}
      elementComputedStyles={imageComputedStyles}
      tooltipText={`X:${imageOffset.x}% Y:${imageOffset.y}%`}
      topbarHeight={topbarHeight}
    />
  );
}

BackgroundImageContainer.propTypes = {
  elementId: PropTypes.string.isRequired,
  onOverlayClick: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onDrag: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
  onScale: PropTypes.func,
  width: PropTypes.number,
  height: PropTypes.number,
  orientation: PropTypes.string,
  preserveSharedData: PropTypes.bool,
  shouldApplyChangesOnSubmit: PropTypes.bool,
};

export default withFocalPointContext(BackgroundImageContainer);
